/**
 * Widget Store - Preact Signals for state management
 */

import { signal, computed } from '@preact/signals';
import type { Message, ServerConfig, WidgetState } from './types';

// Core state signals
export const isOpen = signal(false);
export const isLoading = signal(true);
export const isConnected = signal(false);
export const isTyping = signal(false);
export const typingName = signal<string | undefined>(undefined);
export const messages = signal<Message[]>([]);
export const unreadCount = signal(0);
export const humanHandling = signal(false);
export const humanHandlerName = signal<string | null>(null);
export const awaitingResponse = signal(false);
export const serverConfig = signal<ServerConfig | null>(null);
export const error = signal<string | null>(null);
export const transferCountdown = signal<number>(0); // Seconds remaining (0 = no timer)

// Session data
export const visitorToken = signal<string | null>(null);
export const visitorId = signal<string | null>(null);
export const conversationId = signal<string | null>(null);

// Computed values
export const hasMessages = computed(() => messages.value.length > 0);
export const lastMessage = computed(() =>
  messages.value.length > 0 ? messages.value[messages.value.length - 1] : null
);

// Actions
export function openWidget() {
  isOpen.value = true;
  unreadCount.value = 0;
}

export function closeWidget() {
  isOpen.value = false;
}

export function toggleWidget() {
  if (isOpen.value) {
    closeWidget();
  } else {
    openWidget();
  }
}

export function addMessage(message: Message) {
  // Deduplicate by ID to prevent double-adding from API response + SSE
  const exists = messages.value.some((m) => m.id === message.id);
  if (exists) {
    return;
  }

  messages.value = [...messages.value, message];

  // Increment unread if message is from agent/system and user isn't actively viewing
  if (message.senderType !== 'visitor') {
    const tabHidden = typeof document !== 'undefined' && document.hidden;
    if (!isOpen.value || tabHidden) {
      unreadCount.value += 1;
    }
  }
}

/**
 * Add an optimistic message (shown immediately, before API confirms)
 * Returns the temp ID so it can be updated later
 */
export function addOptimisticMessage(content: string, contentType: 'text' | 'image' = 'text', imageUrl?: string): string {
  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const message: Message = {
    id: tempId,
    content: contentType === 'image' && imageUrl ? imageUrl : content,
    contentType,
    senderType: 'visitor',
    senderName: null,
    isAiResponse: false,
    createdAt: new Date().toISOString(),
    caption: contentType === 'image' ? content : undefined,
  };

  messages.value = [...messages.value, message];
  return tempId;
}

/**
 * Update an optimistic message with real data from server
 */
export function updateOptimisticMessage(tempId: string, realMessage: Message) {
  messages.value = messages.value.map((m) =>
    m.id === tempId ? realMessage : m
  );
}

/**
 * Remove a failed optimistic message
 */
export function removeOptimisticMessage(tempId: string) {
  messages.value = messages.value.filter((m) => m.id !== tempId);
}

/** Replace one message's content (a picture's link, once its signed link has been refreshed). */
export function updateMessageContent(messageId: string, content: string) {
  messages.value = messages.value.map((m) => (m.id === messageId ? { ...m, content } : m));
}

export function setMessages(newMessages: Message[]) {
  messages.value = newMessages;
}

export function setConfig(config: ServerConfig) {
  serverConfig.value = config;
}

export function setSession(token: string, vId: string, cId: string) {
  visitorToken.value = token;
  visitorId.value = vId;
  conversationId.value = cId;

  // Store in localStorage for session persistence
  try {
    localStorage.setItem('chuck_widget_token', token);
    localStorage.setItem('chuck_widget_visitor_id', vId);
    localStorage.setItem('chuck_widget_conversation_id', cId);
  } catch (e) {
    // localStorage not available
  }
}

export function loadSession(): { token: string | null; visitorId: string | null; conversationId: string | null } {
  try {
    return {
      token: localStorage.getItem('chuck_widget_token'),
      visitorId: localStorage.getItem('chuck_widget_visitor_id'),
      conversationId: localStorage.getItem('chuck_widget_conversation_id'),
    };
  } catch (e) {
    return { token: null, visitorId: null, conversationId: null };
  }
}

export function clearSession() {
  visitorToken.value = null;
  visitorId.value = null;
  conversationId.value = null;
  messages.value = [];

  try {
    localStorage.removeItem('chuck_widget_token');
    localStorage.removeItem('chuck_widget_visitor_id');
    localStorage.removeItem('chuck_widget_conversation_id');
  } catch (e) {
    // localStorage not available
  }
}

export function setTyping(typing: boolean, name?: string) {
  isTyping.value = typing;
  typingName.value = name;
}

export function setHumanHandling(handling: boolean, handlerName?: string) {
  humanHandling.value = handling;
  if (handling && handlerName) {
    humanHandlerName.value = handlerName;
  } else if (!handling) {
    humanHandlerName.value = null;
  }
}

export function setConversationId(cId: string) {
  conversationId.value = cId;
  try {
    localStorage.setItem('chuck_widget_conversation_id', cId);
  } catch (e) {
    // localStorage not available
  }
}

export function setError(err: string | null) {
  error.value = err;
}

export function setLoading(loading: boolean) {
  isLoading.value = loading;
}

export function setConnected(connected: boolean) {
  isConnected.value = connected;
}

export function setAwaitingResponse(awaiting: boolean) {
  awaitingResponse.value = awaiting;
}

// Get current state as object
export function getState(): WidgetState {
  return {
    isOpen: isOpen.value,
    isLoading: isLoading.value,
    isConnected: isConnected.value,
    isTyping: isTyping.value,
    typingName: typingName.value,
    messages: messages.value,
    unreadCount: unreadCount.value,
    humanHandling: humanHandling.value,
    humanHandlerName: humanHandlerName.value,
    awaitingResponse: awaitingResponse.value,
    config: serverConfig.value,
    error: error.value,
  };
}
